import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { ArrowRight, ShieldCheck } from "lucide-react";
import BrandIcon from "../components/app/BrandIcon";

export default function LoginPage() {
  // Non è una landing SEO: noindex (esclusa anche dalla sitemap).
  useEffect(() => {
    let m = document.head.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "robots"); document.head.appendChild(m); }
    m.setAttribute("content", "noindex, follow");
    return () => { m.remove(); };
  }, []);
  const { t } = useTranslation();
  const { login, verify2faLogin, error, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const resetSuccess = params.get("reset") === "ok";

  // 2FA state
  const [twofaToken, setTwofaToken] = useState("");
  const [twofaCode, setTwofaCode] = useState("");
  const [twofaDevHint, setTwofaDevHint] = useState("");
  const [twofaError, setTwofaError] = useState("");

  // Live catalog stats — single source of truth, never hardcoded so the hero
  // always shows the real catalog size as the dataset grows.
  const [catalogStats, setCatalogStats] = useState(null);
  useEffect(() => {
    api.get("/catalog/stats").then((r) => setCatalogStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && typeof user === "object") {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const [resendMsg, setResendMsg] = useState("");
  const [showResend, setShowResend] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResendMsg("");
    setShowResend(false);
    setTwofaError("");
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) navigate("/dashboard");
    else if (res.code === "EMAIL_NOT_VERIFIED") setShowResend(true);
    else if (res.code === "TWOFA_REQUIRED") {
      setTwofaToken(res.challengeToken);
      if (res.devCode) setTwofaDevHint(res.devCode);
    }
  };

  const submitTwofa = async (e) => {
    e.preventDefault();
    setTwofaError("");
    setBusy(true);
    const res = await verify2faLogin(twofaToken, twofaCode);
    setBusy(false);
    if (res.ok) navigate("/dashboard");
    else setTwofaError(t("auth.twofaInvalidCode"));
  };

  const handleResend = async () => {
    setResendMsg("");
    try {
      const { data } = await (await import("../lib/api")).default.post("/auth/resend-verification", { email });
      setResendMsg(data.dev_verification_url
        ? `${t("auth.resendPreviewPrefix")}: ${data.dev_verification_url}`
        : t("auth.resendSuccess"));
    } catch {
      setResendMsg(t("auth.resendErrorGeneric"));
    }
  };

  return (
    <div className="theme-swiss min-h-screen grid lg:grid-cols-2 bg-slate-900">
      {/* Left - Hero panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1713371398485-7bde1bde9def?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-white/55 bg-gradient-to-br from-slate-900/80 via-slate-900/60 to-slate-900/95" />

        <div className="relative z-10">
          <Link
            to="/"
            className="inline-flex items-center gap-3 mb-12 group"
            data-testid="hero-logo-home"
          >
            <BrandIcon size={40} />
            <div>
              <div className="font-display font-bold text-2xl text-slate-100 leading-none group-hover:text-orange-300 transition-colors">MachinePilot</div>
              <div className="label-tech mt-1">CAM ESTIMATOR · v1.0</div>
            </div>
          </Link>

          <h1 className="font-display font-bold text-4xl lg:text-5xl text-slate-100 leading-tight max-w-xl">
            {t("landing.heroTitle1")} {t("landing.heroTitle2")}<br/>
            <span className="text-blue-400">{t("landing.heroTitle3")}</span>
          </h1>
          <p className="mt-6 text-slate-300 max-w-lg">{t("landing.heroSub")}</p>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-4">
          {[
            { k: catalogStats ? String(catalogStats.machine_models) : "…", v: t("nav.machines") },
            { k: catalogStats ? String(catalogStats.materials) : "…", v: t("newQuote.stepLabel2") },
            { k: "AI", v: t("landing.features.vision.title") },
          ].map((s) => (
            <div key={s.v} className="border border-slate-700/60 bg-slate-900/40 backdrop-blur-sm rounded-md p-4">
              <div className="font-mono-data text-3xl text-blue-400" data-testid={`hero-stat-${s.v}`}>{s.k}</div>
              <div className="label-tech mt-1 text-[10px]">{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-slate-900">
        {!twofaToken ? (
        <form onSubmit={submit} className="w-full max-w-md fade-in-up" data-testid="login-form">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-8 group" data-testid="mobile-logo-home">
            <BrandIcon size={40} />
            <div className="font-display font-bold text-xl text-slate-100 group-hover:text-orange-300 transition-colors">MachinePilot</div>
          </Link>

          <div className="label-tech mb-2">{t("auth.accessTitle")}</div>
          <h2 className="font-display font-bold text-3xl text-slate-100">{t("auth.welcome")}</h2>
          <p className="text-slate-400 mt-2 text-sm">{t("auth.accessSubtitle")}</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="label-tech block mb-2">{t("auth.email")}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 rounded-md text-sm" required data-testid="login-email"/>
            </div>
            <div>
              <label className="label-tech block mb-2">{t("auth.password")}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 rounded-md text-sm" required minLength={6} data-testid="login-password"/>
            </div>
          </div>

          {error && (
            <div className="mt-4 px-4 py-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-sm" data-testid="login-error">{error}</div>
          )}

          {resetSuccess && !error && (
            <div className="mt-4 px-4 py-3 rounded-md bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 text-sm" data-testid="reset-success-banner">
              {t("auth.resetSuccessBanner")}
            </div>
          )}

          {showResend && (
            <div className="mt-3 px-4 py-3 rounded-md bg-amber-500/10 border border-amber-500/40 text-amber-200 text-sm space-y-2" data-testid="resend-verification-box">
              <p>{t("auth.didntReceive")}</p>
              <button type="button" onClick={handleResend} className="text-amber-300 hover:text-amber-100 font-medium underline" data-testid="resend-verification-btn">
                {t("auth.resendLink")}
              </button>
              {resendMsg && <div className="text-xs text-emerald-300 break-all mt-2" data-testid="resend-result">{resendMsg}</div>}
            </div>
          )}

          <button type="submit" disabled={busy} className="btn-primary w-full mt-8 px-5 py-3 rounded-md flex items-center justify-center gap-2" data-testid="login-submit">
            {busy ? t("auth.loggingIn") : (<>{t("auth.login")} <ArrowRight size={16}/></>)}
          </button>

          <div className="mt-4 text-sm text-center">
            <Link to="/forgot-password" className="text-slate-400 hover:text-blue-300" data-testid="forgot-password-link">
              {t("auth.forgotPasswordLink")}
            </Link>
          </div>

          <div className="mt-6 text-sm text-slate-400 text-center">
            {t("auth.noAccount")}{" "}
            <Link to="/register" className="text-blue-400 hover:text-blue-300 font-medium" data-testid="register-link">
              {t("auth.registerCta")}
            </Link>
          </div>
        </form>
        ) : (
        <form onSubmit={submitTwofa} className="w-full max-w-md fade-in-up" data-testid="login-2fa-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <BrandIcon size={40} />
            <div className="font-display font-bold text-xl text-slate-100">MachinePilot</div>
          </div>

          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs mb-3">
            <ShieldCheck size={12}/> {t("auth.twofaBadge")}
          </div>
          <h2 className="font-display font-bold text-3xl text-slate-100">{t("auth.twofaTitle")}</h2>
          <p className="text-slate-400 mt-2 text-sm">{t("auth.twofaSubtitle", { email })}</p>

          <div className="mt-8">
            <label className="label-tech block mb-2">{t("auth.twofaCodeLabel")}</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={twofaCode}
              onChange={(e) => setTwofaCode(e.target.value.replace(/\D/g, ""))}
              className="w-full px-4 py-3 rounded-md text-2xl font-mono tracking-[0.5em] text-center"
              placeholder="••••••"
              required
              autoFocus
              data-testid="login-2fa-code"
            />
            {twofaDevHint && (
              <div className="mt-2 text-xs text-amber-300" data-testid="login-2fa-dev-hint">
                dev_code: {twofaDevHint}
              </div>
            )}
          </div>

          {twofaError && (
            <div className="mt-4 px-4 py-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-sm" data-testid="login-2fa-error">{twofaError}</div>
          )}

          <button
            type="submit"
            disabled={busy || twofaCode.length !== 6}
            className="btn-primary w-full mt-8 px-5 py-3 rounded-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="login-2fa-submit"
          >
            {busy ? t("auth.loggingIn") : (<>{t("auth.twofaContinue")} <ArrowRight size={16}/></>)}
          </button>

          <div className="mt-6 text-sm text-slate-400 text-center">
            <button
              type="button"
              onClick={() => { setTwofaToken(""); setTwofaCode(""); setTwofaError(""); setTwofaDevHint(""); }}
              className="text-blue-400 hover:text-blue-300 font-medium"
              data-testid="login-2fa-restart"
            >
              {t("auth.twofaRestart")}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
